import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import DataTable from '../../components/admin/DataTable';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { fetchObituaries } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatDate } from '../../utils/formatters';

// 발인일 기준 빈소 상태 도출 (조문중 / 발인완료)
const getFuneralStatus = (departureDate) => {
  if (!departureDate) return { key: 'unknown', label: '미정', badge: 'bg-gray-100 text-gray-700' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dep = new Date(departureDate);
  dep.setHours(0, 0, 0, 0);
  if (dep < today) return { key: 'done', label: '발인완료', badge: 'bg-gray-100 text-gray-600' };
  if (dep.getTime() === today.getTime()) return { key: 'today', label: '오늘 발인', badge: 'bg-red-100 text-red-700' };
  return { key: 'ongoing', label: '조문중', badge: 'bg-green-100 text-green-700' };
};

const ObituaryManagePage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [obituaries, setObituaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');

  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // 단건 id
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const loadObituaries = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchObituaries();
      setObituaries(data);
    } catch (e) {
      console.error('부고 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadObituaries();
  }, []);

  const filteredObituaries = useMemo(() => {
    const now = new Date();
    return obituaries.filter((obit) => {
      const term = searchTerm.toLowerCase();
      const matchSearch =
        !term ||
        obit.deceasedName?.toLowerCase().includes(term) ||
        obit.funeralHome?.toLowerCase().includes(term) ||
        obit.chiefMourner?.toLowerCase().includes(term);

      const status = getFuneralStatus(obit.departureDate).key;
      const matchStatus = statusFilter === 'all' || status === statusFilter;

      let matchPeriod = true;
      if (periodFilter !== 'all' && obit.createdAt) {
        const created = new Date(obit.createdAt);
        const days = periodFilter === 'week' ? 7 : 30;
        const diff = (now - created) / (1000 * 60 * 60 * 24);
        matchPeriod = diff >= 0 && diff < days;
      }
      return matchSearch && matchStatus && matchPeriod;
    });
  }, [obituaries, searchTerm, statusFilter, periodFilter]);

  const confirmSingleDelete = () => {
    setObituaries((prev) => prev.filter((o) => o.id !== deleteTarget));
    setSelectedIds((prev) => prev.filter((id) => id !== deleteTarget));
    showToast('부고가 삭제되었습니다.', 'success');
    setDeleteTarget(null);
  };

  const confirmBulkDelete = () => {
    setObituaries((prev) => prev.filter((o) => !selectedIds.includes(o.id)));
    showToast(`${selectedIds.length}건의 부고가 삭제되었습니다.`, 'success');
    setSelectedIds([]);
    setBulkDeleteOpen(false);
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true, sortType: 'number' },
    { key: 'deceasedName', label: '고인명', sortable: true },
    { key: 'funeralHome', label: '장례식장', sortable: true },
    { key: 'departureDate', label: '발인일', sortable: true, sortType: 'date',
      render: (v) => (v ? formatDate(v, 'yyyy.MM.dd') : '-') },
    {
      key: 'status', label: '상태', sortable: false,
      render: (_, row) => {
        const s = getFuneralStatus(row.departureDate);
        return <span className={`rounded-full px-2 py-1 text-xs font-medium ${s.badge}`}>{s.label}</span>;
      },
    },
    { key: 'chiefMourner', label: '상주', sortable: true },
    { key: 'createdAt', label: '작성일', sortable: true, sortType: 'date',
      render: (v) => (v ? formatDate(v, 'yyyy.MM.dd') : '-') },
    {
      key: 'actions', label: '관리', sortable: false,
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/obituaries/${row.id}/edit`); }}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            수정
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.id); }}
            className="text-sm font-medium text-red-600 hover:text-red-800"
          >
            삭제
          </button>
        </div>
      ),
    },
  ];

  const statusFilters = [
    { key: 'all', label: '전체' },
    { key: 'ongoing', label: '조문중' },
    { key: 'today', label: '오늘 발인' },
    { key: 'done', label: '발인완료' },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900">부고 관리</h1>
        <Button variant="primary" onClick={() => navigate('/obituaries/create/pc')}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" /> 신규 부고 등록
          </span>
        </Button>
      </div>

      {error ? (
        <ErrorState onRetry={loadObituaries} />
      ) : (
        <>
          {/* 검색 + 필터 */}
          <div className="mb-4 space-y-3">
            <input
              type="text"
              placeholder="고인명 / 장례식장 / 상주로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500 md:w-96"
            />
            <div className="flex flex-wrap items-center gap-2">
              {statusFilters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`rounded-md px-3 py-1.5 text-sm ${
                    statusFilter === f.key ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                aria-label="작성 기간 필터"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">전체 기간</option>
                <option value="week">최근 7일 작성</option>
                <option value="month">최근 30일 작성</option>
              </select>
            </div>
          </div>

          {/* 일괄 작업 바 */}
          {selectedIds.length > 0 && (
            <div className="mb-3 flex items-center justify-between rounded-md bg-primary-50 px-4 py-2 text-sm">
              <span className="text-primary-800">{selectedIds.length}건 선택됨</span>
              <button
                onClick={() => setBulkDeleteOpen(true)}
                className="inline-flex items-center gap-1 font-medium text-red-600 hover:text-red-800"
              >
                <Trash2 className="h-4 w-4" /> 선택 삭제
              </button>
            </div>
          )}

          <DataTable
            columns={columns}
            data={filteredObituaries}
            onRowClick={(o) => navigate(`/obituaries/${o.id}`)}
            emptyMessage="조건에 맞는 부고가 없습니다."
            loading={loading}
            pageSize={10}
            selectable
            selectedIds={selectedIds}
            onSelectChange={setSelectedIds}
          />

          {!loading && (
            <div className="mt-4 text-sm text-gray-600">
              총 {filteredObituaries.length}개의 부고가 있습니다.
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmSingleDelete}
        title="부고 삭제"
        message="이 부고를 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다."
        confirmLabel="삭제"
        confirmVariant="danger"
      />
      <ConfirmModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        title="부고 일괄 삭제"
        message={`선택한 ${selectedIds.length}건의 부고를 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.`}
        confirmLabel="삭제"
        confirmVariant="danger"
      />
    </div>
  );
};

export default ObituaryManagePage;
